import { useCallback, useEffect, useMemo, useState } from "react";
import { getStoredAccessToken, refreshToken } from "~/helpers";
import {
  AddToVersionResponse,
  ChatMessageResponse,
  mockWebSocketClient,
  UpdateData,
  VolumeCreationResponse,
  WebSocketClient,
} from "~/lib/wss-client";
import { WS_URL } from "~/utils/apiUrl";
import logger from "~/utils/logger";

type UseWebsocketParams = {
  projectId: string;
  subscribeTo:
    | "document-update-log"
    | "folder-update-log"
    | "chat-message"
    | "add-to-version"
    | "volume-creation";
  chatId?: string;
};

/**
 * React hook for WebSocket connection with automatic token refresh.
 *
 * Manages WebSocket lifecycle, handles token expiration during long-running
 * operations, and provides listeners for real-time updates.
 *
 * @example
 * const { addListener, removeListener } = useWebsocket({
 *   projectId: "prj_123",
 *   subscribeTo: "document-update-log"
 * });
 *
 * useEffect(() => {
 *   const handler = (updates) => console.log(updates);
 *   addListener(handler);
 *   return () => removeListener(handler);
 * }, [addListener, removeListener]);
 */
export const useWebsocket = <T = UpdateData>({
  projectId,
  subscribeTo,
  chatId = "",
}: UseWebsocketParams) => {
  const authToken = getStoredAccessToken() || "";
  const [isConnected, setIsConnected] = useState(false);

  const wsClient = useMemo(() => {
    if (!authToken) {
      return mockWebSocketClient;
    }
    if (!WS_URL) {
      throw new Error("WS_URL is not defined");
    }

    return new WebSocketClient(
      WS_URL,
      {
        token: authToken,
        projectId,
        subscribeTo,
        chatId,
      },
      {
        tokenProvider: () => getStoredAccessToken(),
        onTokenExpired: async () => {
          try {
            const newToken = await refreshToken({ force: true });
            if (!newToken) {
              logger.error("WebSocket token refresh returned null");
            }
          } catch (error) {
            logger.error("WebSocket token refresh failed:", error);
          }
        },
        onOpen: () => setIsConnected(true),
        onClose: () => setIsConnected(false),
      }
    );
  }, [authToken, projectId, subscribeTo, chatId]);

  const connect = useCallback(() => {
    wsClient.connect();
  }, [wsClient]);

  const disconnect = useCallback(() => {
    wsClient.disconnect();
    setIsConnected(false);
  }, [wsClient]);

  useEffect(() => {
    connect();
    return () => {
      setIsConnected(false);
      if ("destroy" in wsClient && typeof wsClient.destroy === "function") {
        (wsClient as any).destroy();
      } else {
        disconnect();
      }
    };
  }, [connect, disconnect, wsClient]);

  const addListener = useCallback(
    (callback: (updates: T[]) => void) => {
      wsClient.addListener(callback);
    },
    [wsClient]
  );

  const removeListener = useCallback(
    (callback: (updates: T[]) => void) => {
      wsClient.removeListener(callback);
    },
    [wsClient]
  );

  const addChatListener = useCallback(
    (callback: (response: ChatMessageResponse) => void) => {
      wsClient.addChatListener(callback);
    },
    [wsClient]
  );

  const removeChatListener = useCallback(
    (callback: (response: ChatMessageResponse) => void) => {
      wsClient.removeChatListener(callback);
    },
    [wsClient]
  );

  const sendChatMessage = useCallback(
    (organizationId: string, message: string) => {
      wsClient.sendChatMessage(organizationId, message);
    },
    [wsClient]
  );

  const addVolumeCreationListener = useCallback(
    (callback: (response: VolumeCreationResponse) => void) => {
      wsClient.addVolumeCreationListener(callback);
    },
    [wsClient]
  );

  const removeVolumeCreationListener = useCallback(
    (callback: (response: VolumeCreationResponse) => void) => {
      wsClient.removeVolumeCreationListener(callback);
    },
    [wsClient]
  );

  const sendVolumeCreation = useCallback(
    (
      organizationId: string,
      sourceCaseId: string,
      name: string,
      documentIds: string[]
    ) => {
      wsClient.sendVolumeCreation(
        organizationId,
        sourceCaseId,
        name,
        documentIds
      );
    },
    [wsClient]
  );

  const addToVersionListener = useCallback(
    (callback: (response: AddToVersionResponse) => void) => {
      wsClient.addToVersionListener(callback);
    },
    [wsClient]
  );

  const removeToVersionListener = useCallback(
    (callback: (response: AddToVersionResponse) => void) => {
      wsClient.removeToVersionListener(callback);
    },
    [wsClient]
  );

  const sendAddToVersion = useCallback(
    (
      organizationId: string,
      sourceVolumeId: string,
      targetVolumeId: string,
      documentIds: string[]
    ) => {
      wsClient.sendAddToVersion(
        organizationId,
        sourceVolumeId,
        targetVolumeId,
        documentIds
      );
    },
    [wsClient]
  );

  return {
    isConnected,
    connect,
    disconnect,
    addListener,
    removeListener,
    addChatListener,
    removeChatListener,
    sendChatMessage,
    addVolumeCreationListener,
    removeVolumeCreationListener,
    sendVolumeCreation,
    addToVersionListener,
    removeToVersionListener,
    sendAddToVersion,
  };
};
